import { SbbButton, SbbSecondaryButton } from "@sbb-esta/lyne-react/button";
import { SbbCheckbox } from "@sbb-esta/lyne-react/checkbox";
import { SbbCheckboxGroup } from "@sbb-esta/lyne-react/checkbox-group";
import { useTranslations } from "providers";
import { useCallback, useMemo, useState } from "react";

import { LeanButton } from "./LeanButton";
import styles from "./LeanFilterDialog.module.scss";
import { SearchField } from "./LeanSearchField";

export interface IFilterDialogProps {
  columnIndex: number;
  anchorRect: DOMRect;
  checkboxes?: string[];
  initialCheckboxes?: string[];
  onHide?: () => void;
  onSave: (selectedCheckboxes: string[] | undefined) => void;
  onCancel: () => void;
}

export const LeanFilterDialog: React.FC<IFilterDialogProps> = ({
  columnIndex,
  anchorRect,
  checkboxes,
  initialCheckboxes,
  onSave,
  onHide,
  onCancel,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedCheckboxes, setSelectedCheckboxes] = useState<string[] | undefined>(initialCheckboxes);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(false);

  const i18n = useTranslations();

  const filteredCheckboxes = useCallback(
    (options: string): boolean => {
      if (searchValue === "") return true;
      return options.toLowerCase().indexOf(searchValue.toLowerCase()) > -1;
    },
    [searchValue]
  );

  const handleCheckboxChange = useMemo(
    () =>
      checkboxes
        ? (key: string) => {
            const localSelectedCheckboxes = selectedCheckboxes ? [...selectedCheckboxes] : undefined;
            if (localSelectedCheckboxes === undefined) {
              setSelectedCheckboxes(checkboxes.filter((c) => c !== key));
              return;
            }

            const find = localSelectedCheckboxes.indexOf(key);
            if (find > -1) {
              localSelectedCheckboxes.splice(find, 1);
            } else {
              localSelectedCheckboxes.push(key);
            }
            setSaveButtonDisabled(localSelectedCheckboxes.length === 0);
            setSelectedCheckboxes(localSelectedCheckboxes);
          }
        : undefined,
    [checkboxes, selectedCheckboxes]
  );

  const allSelected = (): boolean => {
    if (selectedCheckboxes === undefined) return true;
    return !!checkboxes && checkboxes.every((c) => selectedCheckboxes?.includes(c));
  };

  const handleSelectAllChange = () => {
    if (allSelected()) {
      setSelectedCheckboxes([]);
      setSaveButtonDisabled(true);
    } else {
      setSelectedCheckboxes(checkboxes);
      setSaveButtonDisabled(false);
    }
  };

  const dropdownStyle = {
    top: anchorRect.bottom + 4,
    left: anchorRect.left,
  };

  return (
    <>
      <div className={styles.backdrop} onClick={onCancel} />
      <div className={styles.dropdown} style={dropdownStyle} key={columnIndex}>
        <div className={styles.dropdownHeader}>{i18n.Filter_Combobox_Filter_Customize}</div>
        <div className={styles.dropdownBody}>
          {onHide && <LeanButton text="Ausblenden" onClick={onHide} mode="primaryAlt" />}
          {checkboxes && (
            <>
              <div className={styles.searchWrapper}>
                <SearchField
                  autoFocus
                  value={searchValue}
                  onChange={setSearchValue}
                  placeholder={i18n.Filter_Search_Placeholder}
                />
              </div>
              <SbbCheckboxGroup orientation="vertical" size="s" style={{ gap: "2px !important" }}>
                <SbbCheckbox
                  checked={selectedCheckboxes === undefined || allSelected()}
                  onChange={handleSelectAllChange}
                >
                  {i18n.SelectAll}
                </SbbCheckbox>
                {checkboxes.map(
                  (item, index) =>
                    filteredCheckboxes(item) && (
                      <SbbCheckbox
                        size="s"
                        key={item + index}
                        checked={selectedCheckboxes === undefined || selectedCheckboxes?.includes(item)}
                        onChange={() => handleCheckboxChange?.(item)}
                      >
                        {item}
                      </SbbCheckbox>
                    )
                )}
              </SbbCheckboxGroup>
            </>
          )}
        </div>
        {checkboxes && (
          <div className={styles.dropdownFooter}>
            <SbbSecondaryButton title={i18n.Cancel} size="s" onClick={onCancel}>
              {i18n.Cancel}
            </SbbSecondaryButton>
            <SbbButton
              size="s"
              title={i18n.Save}
              onClick={() => onSave(selectedCheckboxes)}
              disabled={saveButtonDisabled}
            >
              {i18n.Save}
            </SbbButton>
          </div>
        )}
      </div>
    </>
  );
};
